import type {
  MiceCandidate,
  MiceChosenFlight,
  MiceRecommendation,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';

type Direction = 'OUTWARD' | 'RETURN';

export interface DirectionChange {
  direction: Direction;
  from: MiceChosenFlight | null;
  to: MiceChosenFlight | null;
}

export type ConversationEntry =
  | { kind: 'agent'; rec: MiceRecommendation; diff: DirectionChange[]; current: boolean }
  | { kind: 'operator'; text: string; at: string };

/** O comentário que o servidor grava na versão nascida de uma escolha do operador
 *  (`apps/services/mice/src/workers/recommendation/operator_choice.ts`): não é texto do operador. */
const OPERATOR_CHOICE_COMMENT = 'Escolhido pelo operador';

const DIRECTIONS: readonly Direction[] = ['OUTWARD', 'RETURN'];
const LOWER_LABEL = { OUTWARD: 'mice.activity.outbound', RETURN: 'mice.activity.inbound' } as const;

const flightOf = (rec: MiceRecommendation, direction: Direction): MiceChosenFlight | null =>
  direction === 'OUTWARD' ? rec.outbound : rec.inbound;

/** Companhia, número e partida do primeiro trecho: o que faz dois voos serem o mesmo. */
const identityOf = (flight: MiceChosenFlight | null): string | null => {
  const hop = flight?.hops[0];
  return hop ? `${hop.carrier}${hop.flightNumber}@${hop.departAt}` : null;
};

/** "LA 3410 15:30": os voos com as conexões e a partida, lida do texto (o `Z` é espúrio). */
export const flightLabel = (flight: Pick<MiceCandidate, 'hops' | 'departAt'>): string => {
  const flights = flight.hops.map((h) => `${h.carrier} ${h.flightNumber}`).join(' + ');
  return `${flights} ${flight.departAt.slice(11, 16)}`;
};

/** Os sentidos cujo voo mudou de uma versão para a seguinte; sem anterior, os dois sentidos. */
export function versionDiff(
  prev: MiceRecommendation | null,
  curr: MiceRecommendation,
): DirectionChange[] {
  return DIRECTIONS.flatMap((direction) => {
    const to = flightOf(curr, direction);
    if (prev === null) return [{ direction, from: null, to }];
    const from = flightOf(prev, direction);
    return identityOf(from) === identityOf(to) ? [] : [{ direction, from, to }];
  });
}

/** "Escolheu LA 3418 18:05 na volta" por sentido que a escolha do operador trocou. A decisão
 *  `operator_choice` fica no sentido não tocado das escolhas seguintes, então só vale o que mudou;
 *  escolher o próprio voo pedido grava `kept`, então o comentário da escolha também identifica. */
function choiceTexts(rec: MiceRecommendation, diff: DirectionChange[]): string[] {
  const byOperator = (direction: Direction): boolean =>
    rec.comment === OPERATOR_CHOICE_COMMENT ||
    rec.directions.some((d) => d.direction === direction && d.decision === 'operator_choice');
  return diff.flatMap(({ direction, to }) =>
    to && byOperator(direction)
      ? [
          t('mice.conversation.chose', {
            flight: flightLabel(to),
            direction: t(LOWER_LABEL[direction]),
          }),
        ]
      : [],
  );
}

/** A conversa da mais antiga para a mais nova: antes de cada versão, o que o operador pediu
 *  (o comentário do reconsiderar) ou escolheu; a última versão é a atual. */
export function conversationEntries(history: MiceRecommendation[]): ConversationEntry[] {
  const versions = [...history].sort((a, b) => a.version - b.version);
  return versions.flatMap((rec, i): ConversationEntry[] => {
    const diff = versionDiff(versions[i - 1] ?? null, rec);
    const asked =
      rec.comment && rec.comment !== OPERATOR_CHOICE_COMMENT
        ? [rec.comment]
        : choiceTexts(rec, diff);
    return [
      ...asked.map((text) => ({ kind: 'operator' as const, text, at: rec.createdAt })),
      { kind: 'agent' as const, rec, diff, current: i === versions.length - 1 },
    ];
  });
}
